/**
 * MRP Event System
 * 
 * Event-driven updates for cross-module relationships
 * Events emitted by MRP operations to notify other modules:
 * - Inventory: Stock movements and projections
 * - Procurement: PR creation and updates
 * - Production: WO creation and scheduling
 * - Notifications: Alerts and digests
 */

// Event Types
export type MRPEventType = 
  | 'mrp.run.started'
  | 'mrp.run.completed' 
  | 'mrp.run.failed'
  | 'mrp.shortage.detected'
  | 'mrp.suggestion.accepted'
  | 'mrp.suggestion.cancelled'
  | 'mrp.planned_order.created'
  | 'mrp.planned_order.converted.pr'
  | 'mrp.planned_order.converted.wo'
  | 'mrp.planned_order.cancelled'
  | 'mrp.stock_out.projected';

// Event Payloads
export interface MRPRunStartedEvent {
  type: 'mrp.run.started';
  payload: {
    run_id: string;
    run_number: string;
    warehouse_id: string;
    horizon_date: string;
    created_by: string;
    timestamp: string;
  };
}

export interface MRPRunCompletedEvent {
  type: 'mrp.run.completed';
  payload: {
    run_id: string;
    run_number: string;
    warehouse_id: string;
    shortages_found: number;
    items_analyzed: number;
    duration_ms: number;
    timestamp: string;
  };
}

export interface MRPShortageDetectedEvent {
  type: 'mrp.shortage.detected';
  payload: {
    run_id: string;
    item_id: string;
    item_code: string;
    item_name: string;
    shortage_qty: number;
    need_by_date: string;
    suggested_type: 'PR' | 'WO';
    is_critical: boolean;
    warehouse_id: string;
    timestamp: string;
  };
}

export interface MRPSuggestionAcceptedEvent {
  type: 'mrp.suggestion.accepted';
  payload: {
    run_id: string;
    shortage_id: string;
    planned_order_id: string;
    item_id: string;
    item_code: string;
    suggested_qty: number;
    need_by_date: string;
    type: 'PR' | 'WO';
    accepted_by: string;
    timestamp: string;
  };
}

export interface MRPPlannedOrderConvertedEvent {
  type: 'mrp.planned_order.converted.pr' | 'mrp.planned_order.converted.wo';
  payload: {
    planned_order_id: string;
    target_id: string; // PR ID or WO ID
    item_id: string;
    item_code: string;
    quantity: number;
    need_by_date: string;
    converted_by: string;
    timestamp: string;
  };
}

export interface MRPStockOutProjectedEvent {
  type: 'mrp.stock_out.projected';
  payload: {
    item_id: string;
    item_code: string;
    item_name: string;
    current_stock: number;
    projected_stock_out_date: string;
    days_until_stock_out: number;
    warehouse_id: string;
    timestamp: string;
  };
}

export type MRPEvent = 
  | MRPRunStartedEvent
  | MRPRunCompletedEvent
  | MRPShortageDetectedEvent
  | MRPSuggestionAcceptedEvent
  | MRPPlannedOrderConvertedEvent
  | MRPStockOutProjectedEvent;

// Event Emitter Class
class MRPEventEmitter {
  private static instance: MRPEventEmitter;
  private listeners: Map<MRPEventType, Array<(event: MRPEvent) => void>> = new Map();

  private constructor() {}

  public static getInstance(): MRPEventEmitter {
    if (!MRPEventEmitter.instance) {
      MRPEventEmitter.instance = new MRPEventEmitter();
    }
    return MRPEventEmitter.instance;
  }

  // Subscribe to events
  public on<T extends MRPEvent>(
    eventType: T['type'], 
    listener: (event: T) => void
  ): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, []);
    }
    
    const typedListener = listener as (event: MRPEvent) => void;
    this.listeners.get(eventType)!.push(typedListener);
    
    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(eventType);
      if (listeners) {
        const index = listeners.indexOf(typedListener);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  // Emit events
  public emit(event: MRPEvent): void {
    const listeners = this.listeners.get(event.type);
    if (listeners) {
      listeners.forEach(listener => {
        try {
          listener(event);
        } catch (error) {
          console.error(`Error in MRP event listener for ${event.type}:`, error);
        }
      });
    }
    
    // Emit to cross-module event channels for ERP integration
    this.emitToCrossModuleChannels(event);
    
    // Log event for debugging
    console.log(`[MRP Event] ${event.type}:`, event.payload);
  }

  // Emit events to cross-module channels as per integration guide
  private async emitToCrossModuleChannels(event: MRPEvent): Promise<void> {
    try {
      const { supabase } = await import('@/lib/supabase/client');
      
      switch (event.type) {
        case 'mrp.run.completed':
          // Emit to planning.events channel for procurement dashboard badges
          await supabase
            .channel('planning.events')
            .send({
              type: 'broadcast',
              event: 'mrp_run_complete',
              payload: {
                run_id: event.payload.run_id,
                run_number: event.payload.run_number,
                shortages_found: event.payload.shortages_found,
                warehouse_id: event.payload.warehouse_id,
                completed_at: event.payload.timestamp
              }
            });
          break;
        
        case 'mrp.suggestion.accepted':
          // Emit to planning.events for KPI updates
          await supabase
            .channel('planning.events')
            .send({
              type: 'broadcast',
              event: 'planned_order.accepted',
              payload: {
                planned_order_id: event.payload.planned_order_id,
                item_code: event.payload.item_code,
                quantity: event.payload.suggested_qty,
                type: event.payload.type
              }
            });
          break;
        
        case 'mrp.planned_order.converted.pr':
        case 'mrp.planned_order.converted.wo':
          const conversionType = event.type.includes('.pr') ? 'PR' : 'WO';
          const channelName = conversionType === 'PR' ? 'procurement.events' : 'workorders.events';
          
          await supabase
            .channel(channelName)
            .send({
              type: 'broadcast',
              event: 'planned_order.converted',
              payload: {
                planned_order_id: event.payload.planned_order_id,
                object_type: conversionType,
                object_id: event.payload.target_id,
                object_code: `${conversionType}-Generated`,
                item_code: event.payload.item_code,
                user_id: event.payload.converted_by
              }
            });
          break;
        
        case 'mrp.shortage.detected':
          // Emit critical shortages to planning.events for notifications
          if (event.payload.is_critical) {
            await supabase
              .channel('planning.events')
              .send({
                type: 'broadcast',
                event: 'critical_shortage_detected',
                payload: {
                  item_code: event.payload.item_code,
                  shortage_qty: event.payload.shortage_qty,
                  need_by_date: event.payload.need_by_date,
                  run_id: event.payload.run_id
                }
              });
          }
          break;
      }
    } catch (error) {
      console.error('Error emitting to cross-module channels:', error);
      // Don't throw - continue with local event processing
    }
  }

  // Remove all listeners for cleanup
  public removeAllListeners(eventType?: MRPEventType): void {
    if (eventType) {
      this.listeners.delete(eventType);
    } else {
      this.listeners.clear();
    }
  }
}

// Export singleton instance
export const mrpEvents = MRPEventEmitter.getInstance();

// Helper functions for common event emissions
export const emitMRPRunStarted = (data: MRPRunStartedEvent['payload']) => {
  mrpEvents.emit({
    type: 'mrp.run.started',
    payload: data
  });
};

export const emitMRPRunCompleted = (data: MRPRunCompletedEvent['payload']) => {
  mrpEvents.emit({
    type: 'mrp.run.completed',
    payload: data
  });
};

export const emitMRPShortageDetected = (data: MRPShortageDetectedEvent['payload']) => {
  mrpEvents.emit({
    type: 'mrp.shortage.detected',
    payload: data
  });
};

export const emitMRPSuggestionAccepted = (data: MRPSuggestionAcceptedEvent['payload']) => {
  mrpEvents.emit({
    type: 'mrp.suggestion.accepted',
    payload: data
  });
};

export const emitMRPPlannedOrderConverted = (
  type: 'pr' | 'wo',
  data: Omit<MRPPlannedOrderConvertedEvent['payload'], 'timestamp'>
) => {
  mrpEvents.emit({
    type: type === 'pr' ? 'mrp.planned_order.converted.pr' : 'mrp.planned_order.converted.wo',
    payload: {
      ...data,
      timestamp: new Date().toISOString()
    }
  });
};

export const emitMRPStockOutProjected = (data: MRPStockOutProjectedEvent['payload']) => {
  mrpEvents.emit({
    type: 'mrp.stock_out.projected',
    payload: data
  });
};
